import React from "react";
import { useNewClientWizardContext } from "../Context/NewClientWizardContext";
import styles from "../NewClientWizard.module.css";
import { validateAll } from "../utils/validations";
import { ProvisionClientProductsResult, WizardClient, WizardProduct } from "../types";

interface ReviewStepProps {
  client: WizardClient;
  product: WizardProduct;
  onProvision: () => void;
  provisioning: boolean;
  result: ProvisionClientProductsResult | null;
}

/** Step 4 — summary, the Provision button, and per-step results. */
export function ReviewStep({ client, product, onProvision, provisioning, result }: ReviewStepProps) {
  const { formData } = useNewClientWizardContext();
  const ready = validateAll(formData);

  if (result) {
    return (
      <div className={styles.stepBody}>
        <h4>{result.success ? "Product provisioned" : "Provisioning finished with problems"}</h4>
        <p>
          Client <b>{result.clientName}</b> ({result.clientGuid})
        </p>
        {result.products.map((productResult) => (
          <div key={productResult.productGuid} className={styles.productConfigBlock}>
            <h5>
              {productResult.productName || productResult.productGuid} —{" "}
              {productResult.enabled ? "enabled" : productResult.provisioned ? "provisioned, not enabled" : "failed"}
            </h5>
            {productResult.error && <div className={styles.error}>{productResult.error}</div>}
            {productResult.provisioning?.transportError && (
              <div className={styles.error}>{productResult.provisioning.transportError}</div>
            )}
            {productResult.provisioning?.steps && productResult.provisioning.steps.length > 0 && (
              <ul className={styles.stepsList}>
                {productResult.provisioning.steps.map((step, index) => (
                  <li key={index}>
                    {step.success ? "✓" : "✗"} {step.step}
                    {step.error ? ` — ${step.error}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
        {result.success && (
          <p className={styles.hint}>
            Next: assign users and their account-type / DTC permissions from the
            Client-Products &gt; Users tab.
          </p>
        )}
        {!result.success && (
          <p className={styles.hint}>
            Nothing was enabled for this product. Fix the reported issue and run the
            wizard again, or configure it manually via Client-Products.
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={styles.stepBody}>
      <h4>Review</h4>
      <ul>
        <li><b>Client:</b> {client.name}</li>
        <li>
          <b>Product:</b> {product.name} → database <b>{formData.config.databaseCatalog || "?"}</b>{" "}
          on {formData.config.databaseServer || "?"}
          {formData.config.elasticPoolName ? <> (elastic pool <b>{formData.config.elasticPoolName}</b>)</> : null}
        </li>
        <li>
          <b>Auth database:</b> {formData.config.authDatabaseCatalog || "?"} on{" "}
          {formData.config.authDatabaseServer || "?"}
        </li>
        <li><b>Firm profile:</b> {formData.firmProfile.companyName || "—"} (DTC {formData.firmProfile.dtcNo || "—"})</li>
        <li><b>Account types:</b> {formData.accountTypes.map((a) => a.abbreviation).join(", ") || "—"}</li>
        <li><b>Associated DTCs:</b> {formData.associatedDtcs.map((d) => d.dtcNumber).join(", ") || "—"}</li>
      </ul>

      {!ready && (
        <div className={styles.error}>
          Complete the required fields in the earlier steps before provisioning.
        </div>
      )}

      {ready && (formData.accountTypes.length === 0 || formData.associatedDtcs.length === 0) && (
        <p className={styles.hint}>
          Note: {formData.accountTypes.length === 0 ? "no account types" : ""}
          {formData.accountTypes.length === 0 && formData.associatedDtcs.length === 0 ? " and " : ""}
          {formData.associatedDtcs.length === 0 ? "no associated DTCs" : ""} entered — users can
          still be assigned, but will have no catalog permissions to bind until these are
          created in the app afterwards.
        </p>
      )}

      <button
        type="button"
        className={styles.provisionButton}
        disabled={!ready || provisioning}
        onClick={onProvision}
      >
        {provisioning ? "Provisioning… this can take a few minutes" : "Provision Product"}
      </button>
      <p className={styles.hint}>
        Provisioning creates the database from the source-controlled baseline, seeds the
        firm profile, account types and DTCs, saves this configuration, and enables the
        client-product.
      </p>
    </div>
  );
}
